import { api } from "./api";

export type FolderRecord = { id: string; name: string; parent_id: string | null; path: string };

export type FolderUploadItem = {
  file: File;
  folderId: string | null;
  folderLabel: string;
};

type PlannedUpload = {
  file: File;
  dirSegments: string[];
};

const ACCEPTED_EXT =
  /\.(pdf|docx?|xlsx?|pptx?|csv|png|jpe?g|webp|gif)$/i;

function relativePath(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return rel?.trim() || file.name;
}

function hasAcceptedExtension(name: string): boolean {
  return ACCEPTED_EXT.test(name);
}

function planFolderUpload(files: File[]): PlannedUpload[] {
  const planned: PlannedUpload[] = [];
  for (const file of files) {
    if (!hasAcceptedExtension(file.name)) continue;
    const parts = relativePath(file).split("/").filter(Boolean);
    if (!parts.length) continue;
    parts.pop();
    planned.push({ file, dirSegments: parts });
  }
  return planned;
}

function findChildFolder(folders: FolderRecord[], parentId: string | null, name: string): FolderRecord | undefined {
  return folders.find((f) => f.parent_id === parentId && f.name === name);
}

function folderLabelFromPath(path: string): string {
  return path.replace(/^\//, "") || "Space root";
}

async function createFolder(
  roomId: string,
  parentId: string | null,
  name: string,
): Promise<FolderRecord> {
  const res = await api<{ folder: FolderRecord }>(`/api/v1/rooms/${roomId}/folders`, {
    method: "POST",
    body: JSON.stringify({ name, parentId }),
  });
  return res.folder;
}

/**
 * Ensure nested folders exist under the current browser folder and map each file
 * to its target folder id.
 */
export async function resolveFolderUploadItems(
  roomId: string,
  anchorFolderId: string | null,
  anchorLabel: string,
  folders: FolderRecord[],
  files: File[],
): Promise<{ items: FolderUploadItem[]; folders: FolderRecord[] }> {
  const planned = planFolderUpload(files);
  if (!planned.length) {
    throw new Error("no_supported_files");
  }

  let nextFolders = [...folders];
  const pathToId = new Map<string, string | null>();
  pathToId.set("", anchorFolderId);

  const folderPaths = new Set<string>();
  for (const row of planned) {
    for (let i = 1; i <= row.dirSegments.length; i++) {
      folderPaths.add(row.dirSegments.slice(0, i).join("/"));
    }
  }

  const sortedPaths = [...folderPaths].sort((a, b) => a.split("/").length - b.split("/").length);
  for (const relPath of sortedPaths) {
    const segments = relPath.split("/");
    const name = segments[segments.length - 1]!;
    const parentKey = segments.slice(0, -1).join("/");
    const parentId = pathToId.get(parentKey) ?? anchorFolderId;

    const existing = findChildFolder(nextFolders, parentId, name);
    if (existing) {
      pathToId.set(relPath, existing.id);
      continue;
    }

    const created = await createFolder(roomId, parentId, name);
    nextFolders = [...nextFolders, created];
    pathToId.set(relPath, created.id);
  }

  const items: FolderUploadItem[] = planned.map((row) => {
    const key = row.dirSegments.join("/");
    const targetId = key ? (pathToId.get(key) ?? anchorFolderId) : anchorFolderId;
    const folder = nextFolders.find((f) => f.id === targetId);
    const label = folder ? folderLabelFromPath(folder.path) : anchorLabel;
    return { file: row.file, folderId: targetId, folderLabel: label };
  });

  return { items, folders: nextFolders };
}

/** Collect files from a dropped folder tree (Chrome / Edge / Safari). */
export async function collectDroppedUploadFiles(dataTransfer: DataTransfer): Promise<File[]> {
  const items = [...dataTransfer.items];
  if (!items.length) return [...dataTransfer.files];

  const fromEntries: File[] = [];
  let hadEntry = false;

  for (const item of items) {
    const entry = item.webkitGetAsEntry?.();
    if (!entry) continue;
    hadEntry = true;
    fromEntries.push(...(await readEntryFiles(entry, "")));
  }

  if (hadEntry && fromEntries.length) return fromEntries;
  return [...dataTransfer.files];
}

async function readEntryFiles(entry: FileSystemEntry, parentPath: string): Promise<File[]> {
  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntry);
    const rel = parentPath ? `${parentPath}/${file.name}` : file.name;
    try {
      Object.defineProperty(file, "webkitRelativePath", { value: rel, configurable: true });
    } catch {
      // Some browsers lock the property; upload still works for flat drops.
    }
    return hasAcceptedExtension(file.name) ? [file] : [];
  }

  if (!entry.isDirectory) return [];

  const dirPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const collected: File[] = [];

  // readEntries returns batches; keep reading until empty.
  while (true) {
    const batch = await readDirectoryBatch(reader);
    if (!batch.length) break;
    for (const child of batch) {
      collected.push(...(await readEntryFiles(child, dirPath)));
    }
  }

  return collected;
}

function readFileEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectoryBatch(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}
